<!-- src/components/trends/OperationsPanel.vue -->
<!-- 회원권 추세 "운영" 탭: 타일 → 월별 표 → 요일 × 시작 시각 채움률 표 → 가장 빈 수업 → 채움률 차트 -->
<!-- (docs/architecture/admin-trends-design.md 1.7·2.4·4.6절). 불러오기·오류·빈 상태는 TrendsView가 그린다 -->
<template>
  <div class="operations">
    <!-- 타일: 마지막 끝난 달(지난달) 값 + 짧은 정의 -->
    <div class="tiles">
      <div class="tile">
        <span class="tile-k"
          >1인 주간 출석 <small v-if="prev">({{ prevMonthText }})</small></span
        >
        <!-- 값이 없으면(기록 전 달) 단위 없이 "—"만 -->
        <span class="tile-v"
          ><span
            >{{ num1(memberValue(prevAttendance, "per_member_week"))
            }}<small
              v-if="isNum(memberValue(prevAttendance, 'per_member_week'))"
              >회</small
            ></span
          ></span
        >
        <span v-if="prevAttendance && !prevNoData" class="tile-sub">
          출석 {{ count(prevAttendance.attended) }}회 · 회원
          {{ count(prevAttendance.members) }}명
        </span>
        <span class="tile-def">{{ short.per_member_week }}</span>
      </div>

      <div class="tile">
        <span class="tile-k"
          >불참률 <small v-if="prev">({{ prevMonthText }})</small></span
        >
        <span class="tile-v" :class="{ muted: prevNoShowCell.muted }">{{
          prevNoShowCell.main
        }}</span>
        <span v-if="prevNoShowCell.sub" class="tile-sub">{{
          prevNoShowCell.sub
        }}</span>
        <span v-if="prevUnchecked > 0" class="tile-sub"
          >출석 체크 없는 수업 {{ count(prevUnchecked) }}개는 뺐습니다</span
        >
        <span class="tile-def">{{ short.no_show }}</span>
      </div>

      <div class="tile">
        <span class="tile-k"
          >횟수권 소진율 <small v-if="prev">({{ prevMonthText }})</small></span
        >
        <span class="tile-v" :class="{ muted: prevUseUpCell.muted }"
          ><span>{{ prevUseUpCell.main }}</span>
          <span
            v-if="prevCountPass && isNum(prevCountPass.avg_unused)"
            class="tile-extra"
            >평균 {{ num1(prevCountPass.avg_unused) }}회 남김</span
          ></span
        >
        <span v-if="prevUseUpCell.sub" class="tile-sub">{{
          prevUseUpCell.sub
        }}</span>
        <span v-if="prevCountPassByType.length" class="tile-sub by-type">
          <template v-for="(p, i) in prevCountPassByType" :key="p.type"
            ><span v-if="i > 0"> · </span
            ><span :class="['type-part', { 'small-part': p.small }]">{{
              p.text
            }}</span></template
          >
        </span>
        <span class="tile-def">{{ short.use_up }}</span>
      </div>

      <div class="tile">
        <span class="tile-k"
          >정원 채움률 <small v-if="prev">({{ prevMonthText }})</small></span
        >
        <span class="tile-v">{{ pct(prevFill && prevFill.rate) }}</span>
        <span v-if="prevFill && prevFill.classes > 0" class="tile-sub">
          출석 기준 {{ pct(prevFill.attended_rate) }} · 수업
          {{ count(prevFill.classes) }}개
        </span>
        <span class="tile-def">{{ short.fill }}</span>
      </div>
    </div>

    <!-- 월별 표 -->
    <section class="block" aria-labelledby="op-monthly-title">
      <div class="block-head">
        <h3 id="op-monthly-title">월별</h3>
      </div>
      <p class="block-def">
        채움률은 예약 수 ÷ 정원(무료체험 포함), 출석 기준은 출석 ÷ 정원입니다.
        수업 종류별 칸은 예약 기준입니다.
      </p>
      <!-- 좁은 화면에서 옆으로 넘치는 표: 키보드로도 초점을 받아 ←/→로 밀 수 있게(체험 전환 표와 같음, 리뷰 5차) -->
      <div class="table-wrap" role="region" aria-label="월별 표" tabindex="0">
        <table class="t-table">
          <caption class="sr-only">
            최근
            {{
              months
            }}개월 월별 운영 지표
          </caption>
          <thead>
            <tr>
              <th scope="col" class="col-month">월</th>
              <th scope="col">1인 주간 출석</th>
              <th scope="col">불참률</th>
              <th scope="col">횟수권 소진율</th>
              <th scope="col">평균 남김</th>
              <th scope="col">채움률 예약</th>
              <th scope="col">채움률 출석</th>
              <th v-for="t in classTypes" :key="t.value" scope="col">
                {{ t.label }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="r in tableRows"
              :key="r.month"
              :class="{ 'row-partial': r.inProgress }"
            >
              <th scope="row" class="col-month">
                <span class="month">{{ monthLabel(r.month) }}</span>
                <span v-if="r.inProgress" class="badge tone-muted">{{
                  IN_PROGRESS_LABEL
                }}</span>
                <span v-if="r.noData" class="badge tone-muted">{{
                  NO_DATA_LABEL
                }}</span>
              </th>
              <td :class="{ muted: r.noData }">
                <span class="rate">{{ r.perWeek }}</span>
                <span v-if="r.perWeekSub" class="sub">{{ r.perWeekSub }}</span>
              </td>
              <td :class="{ muted: r.noShow.muted }">
                <span class="rate">{{ r.noShow.main }}</span>
                <span v-if="r.noShow.sub" class="sub">{{ r.noShow.sub }}</span>
                <span v-if="r.unchecked > 0" class="sub"
                  >체크 없는 수업 {{ count(r.unchecked) }}개 뺌</span
                >
              </td>
              <td :class="{ muted: r.useUp.muted }" :title="r.useUpTitle">
                <span class="rate">{{ r.useUp.main }}</span>
                <span v-if="r.useUp.sub" class="sub">{{ r.useUp.sub }}</span>
              </td>
              <!-- 평균 남김은 소진율과 같은 끝난 횟수권으로 낸 평균이라 그 수가 5보다 적으면 함께 회색 + 표본 적음(리뷰 5차) -->
              <td :class="{ muted: r.noData || r.avgUnusedSmall }">
                <span class="rate">{{ r.avgUnused }}</span>
                <span v-if="r.avgUnusedSmall" class="sub">{{
                  smallSampleLabel
                }}</span>
              </td>
              <td :title="r.fillTitle">
                <span class="rate">{{ r.fill.main }}</span>
                <span v-if="r.fill.sub" class="sub">{{ r.fill.sub }}</span>
              </td>
              <td>
                <span class="rate">{{ r.fillAttended.main }}</span>
                <span v-if="r.fillAttended.sub" class="sub">{{
                  r.fillAttended.sub
                }}</span>
              </td>
              <td v-for="c in r.byType" :key="c.key" :title="c.title">
                <span class="rate">{{ c.main }}</span>
                <span v-if="c.sub" class="sub">{{ c.sub }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- 요일 × 시작 시각 채움률 (읽기 쉬운 색 표: 칸 글자는 늘 보이고, 색은 거든다) -->
    <section class="block" aria-labelledby="op-slots-title">
      <div class="block-head">
        <h3 id="op-slots-title">요일 × 시작 시각 채움률</h3>
        <div class="slot-controls">
          <div class="seg" role="group" aria-label="기간">
            <span class="seg-k">기간</span>
            <button
              v-for="p in slotPeriods"
              :key="p.value"
              type="button"
              :class="['seg-btn', { active: period === p.value }]"
              :aria-pressed="period === p.value"
              @click="period = p.value"
            >
              {{ p.label }}
            </button>
          </div>
          <div class="seg" role="group" aria-label="기준">
            <span class="seg-k">기준</span>
            <button
              v-for="m in slotMeasures"
              :key="m.value"
              type="button"
              :class="['seg-btn', { active: measure === m.value }]"
              :aria-pressed="measure === m.value"
              @click="measure = m.value"
            >
              {{ m.label }}
            </button>
          </div>
        </div>
      </div>
      <p class="block-def">
        {{ definitions.slots }} 지금 보는 기간: {{ periodText }}.
      </p>

      <template v-if="slotTimes.length">
        <div
          class="table-wrap heat-wrap"
          role="region"
          aria-label="요일 × 시작 시각 표"
          tabindex="0"
        >
          <table class="heat">
            <caption class="sr-only">
              요일 × 시작 시각 정원 채움률 ({{
                periodText
              }},
              {{
                measureLabel
              }})
            </caption>
            <thead>
              <tr>
                <th scope="col" class="heat-time">시각</th>
                <th v-for="d in slotWeekdays" :key="d" scope="col">
                  {{ weekdayLabel(d) }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in heatRows" :key="row.time">
                <th scope="row" class="heat-time">{{ row.time }}</th>
                <td
                  v-for="cell in row.cells"
                  :key="cell.key"
                  :class="['heat-cell', cell.cls]"
                  :style="cell.style"
                  :title="cell.title"
                >
                  {{ cell.text }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 색 범례: 낮음 → 높음, 회색은 지금 시간표에 없는 시각 -->
        <ul class="heat-legend" aria-label="색 범례">
          <li v-for="s in heatLegend" :key="s.label">
            <span
              class="swatch"
              :style="{ background: s.color }"
              aria-hidden="true"
            ></span>
            {{ s.label }}
          </li>
          <li>
            <span class="swatch swatch--off" aria-hidden="true"></span>
            {{ notInScheduleLabel }}
          </li>
          <li><span class="dash">—</span> 수업 없음</li>
        </ul>

        <p class="emptiest">
          <span class="emptiest-k">가장 빈 수업</span>
          <template v-if="emptiest.length">{{ emptiestText }}</template>
          <template v-else
            >수업이 4개 이상인 지금 시간표 칸이 없습니다</template
          >
        </p>
      </template>
      <p v-else class="slot-empty">이 기간에 끝난 수업이 없습니다.</p>
    </section>

    <!-- 정원 채움률 차트(수업 종류 세 계열, 예약 기준) -->
    <section class="chart-card" aria-labelledby="op-chart-title">
      <h3 id="op-chart-title" class="chart-title">
        정원 채움률 (수업 종류별, 예약 기준)
      </h3>
      <TrendChart
        kind="line"
        :labels="labels"
        :series="fillSeries"
        unit="pct"
        :partial-last="hasPartialLast"
        aria-label="정원 채움률(Hybrid·Core·Cardio)"
        :height="220"
      />
    </section>
  </div>
</template>

<script>
import TrendChart from "@/components/trends/TrendChart.vue";
import {
  CLASS_TYPES,
  COUNT_PASS_TYPES,
  HEAT_STEPS,
  IN_PROGRESS_LABEL,
  METRIC_DEFINITIONS,
  METRIC_SHORT,
  NO_DATA_LABEL,
  SLOT_MEASURES,
  SLOT_PERIODS,
  SMALL_SAMPLE_LABEL,
  count,
  emptiestSlots,
  heatStep,
  isSmallSample,
  monthLabel,
  monthLabelShort,
  num1,
  pct,
  pick,
  slotRate,
  weekdayLabel,
} from "@/lib/trends";

const NOT_IN_SCHEDULE_LABEL = "지금 시간표에 없음";

function isNum(x) {
  return typeof x === "number" && Number.isFinite(x);
}

// 비율 칸: 위는 비율, 아래 작은 글은 "(70/860)"와 표본 적음. 분모 0·null이면 "—"
function rateCell(rate, num, den) {
  if (!isNum(rate) || !isNum(den) || den <= 0) {
    return { main: "—", sub: "", muted: false };
  }
  const small = isSmallSample(den);
  const sub = `(${count(num)}/${count(den)})${
    small ? ` · ${SMALL_SAMPLE_LABEL}` : ""
  }`;
  return { main: pct(rate), sub, muted: small };
}

// 채움률 칸: 분자·분모가 커서 괄호 없이 "2,900/3,920"
function fillCell(rate, num, den) {
  if (!isNum(rate) || !isNum(den) || den <= 0) return { main: "—", sub: "" };
  return { main: pct(rate), sub: `${count(num)}/${count(den)}` };
}

function heatPercent(rate) {
  return `${Math.round(rate * 100)}%`;
}

export default {
  name: "OperationsPanel",
  components: { TrendChart },
  props: {
    // GET /v1/admin/trends/operations 응답(2.4절)
    data: { type: Object, required: true },
    months: { type: Number, default: 12 },
  },
  data() {
    return {
      IN_PROGRESS_LABEL,
      NO_DATA_LABEL,
      short: METRIC_SHORT,
      definitions: METRIC_DEFINITIONS,
      classTypes: CLASS_TYPES,
      slotPeriods: SLOT_PERIODS,
      slotMeasures: SLOT_MEASURES,
      notInScheduleLabel: NOT_IN_SCHEDULE_LABEL,
      smallSampleLabel: SMALL_SAMPLE_LABEL,
      // 요일 × 시각 표의 기간·기준(화면 안 상태). 기본은 최근 3개월·예약 기준(설계 검토 C9)
      period: SLOT_PERIODS[0].value,
      measure: SLOT_MEASURES[0].value,
    };
  },
  computed: {
    rows() {
      return Array.isArray(this.data && this.data.rows) ? this.data.rows : [];
    },
    labels() {
      return this.rows.map((r) => r.month);
    },
    hasPartialLast() {
      const last = this.rows[this.rows.length - 1];
      return !!(last && last.in_progress);
    },
    // 타일은 마지막 끝난 달(지난달)
    prev() {
      return this.rows.length > 1 ? this.rows[this.rows.length - 2] : null;
    },
    prevMonthText() {
      return this.prev ? monthLabelShort(this.prev.month) : "";
    },
    prevNoData() {
      return !!(this.prev && this.prev.no_data);
    },
    prevAttendance() {
      return (this.prev && this.prev.attendance) || null;
    },
    prevNoShowCell() {
      const ns = this.prev && !this.prevNoData ? this.prev.no_show : null;
      if (!ns) return { main: "—", sub: "", muted: false };
      return rateCell(ns.rate, ns.missed, ns.booked);
    },
    prevUnchecked() {
      const ns = this.prev && this.prev.no_show;
      return ns && isNum(ns.unchecked_classes) ? ns.unchecked_classes : 0;
    },
    prevCountPass() {
      return this.prev && !this.prevNoData ? this.prev.count_pass : null;
    },
    prevUseUpCell() {
      const cp = this.prevCountPass;
      if (!cp) return { main: "—", sub: "", muted: false };
      return rateCell(cp.use_up_rate, cp.used_up, cp.ended);
    },
    // 종류별 "10회권 75.0% (9/12)" (끝난 횟수권이 있는 종류만). 분모가 5보다 작으면 "(2/2, 표본 적음)"이고 회색
    // (다른 비율과 같은 규칙, R15 (3))
    prevCountPassByType() {
      const cp = this.prevCountPass;
      if (!cp) return [];
      return COUNT_PASS_TYPES.map((t) => {
        const c = pick(cp.by_type, "type", t.value);
        if (!c || !(c.ended > 0)) return null;
        const small = isSmallSample(c.ended);
        const frac = `${count(c.used_up)}/${count(c.ended)}${
          small ? `, ${SMALL_SAMPLE_LABEL}` : ""
        }`;
        return {
          type: t.value,
          text: `${t.label} ${pct(c.use_up_rate)} (${frac})`,
          small,
        };
      }).filter(Boolean);
    },
    prevFill() {
      return (this.prev && this.prev.fill) || null;
    },
    // 월별 표: 최신 달이 위. 회원 지표는 기록 전 달에 "—", 채움률은 수업 자료라 그대로
    tableRows() {
      return this.rows
        .map((r) => {
          const noData = !!r.no_data;
          const att = r.attendance || {};
          const ns = r.no_show || {};
          const cp = r.count_pass || {};
          const fill = r.fill || {};
          // 끝난 횟수권이 없는 종류는 쓰지 않는다("— (0/0) · 평균 —회 남김"이 되지 않게). 분모가 5보다 작으면 타일처럼
          // "(3/3, 표본 적음)"(R15 (3), 리뷰 3차: 칸 title만 작은 표본 비율을 맨숫자로 보였다)
          const useUpTitle = COUNT_PASS_TYPES.map((t) => {
            const c = pick(cp.by_type, "type", t.value);
            if (!c || !(c.ended > 0)) return "";
            const small = isSmallSample(c.ended)
              ? `, ${SMALL_SAMPLE_LABEL}`
              : "";
            return `${t.label} ${pct(c.use_up_rate)} (${count(
              c.used_up
            )}/${count(c.ended)}${small}) · 평균 ${num1(c.avg_unused)}회 남김`;
          })
            .filter(Boolean)
            .join("\n");
          return {
            month: r.month,
            inProgress: !!r.in_progress,
            noData,
            perWeek: noData ? "—" : this.withUnit(num1(att.per_member_week)),
            perWeekSub:
              noData || !isNum(att.member_days) || att.member_days <= 0
                ? ""
                : `출석 ${count(att.attended)} · ${count(att.members)}명`,
            noShow: noData
              ? { main: "—", sub: "", muted: true }
              : rateCell(ns.rate, ns.missed, ns.booked),
            unchecked: noData ? 0 : ns.unchecked_classes || 0,
            useUp: noData
              ? { main: "—", sub: "", muted: true }
              : rateCell(cp.use_up_rate, cp.used_up, cp.ended),
            useUpTitle: noData ? null : useUpTitle || null,
            avgUnused: noData ? "—" : this.withUnit(num1(cp.avg_unused)),
            avgUnusedSmall:
              !noData && isNum(cp.avg_unused) && isSmallSample(cp.ended),
            fill: fillCell(fill.rate, fill.booked, fill.capacity),
            fillAttended: fillCell(
              fill.attended_rate,
              fill.attended,
              fill.capacity
            ),
            fillTitle: isNum(fill.classes)
              ? `수업 ${count(fill.classes)}개 · 예약 ${count(
                  fill.booked
                )} · 출석 ${count(fill.attended)} / 정원 ${count(
                  fill.capacity
                )}`
              : null,
            byType: CLASS_TYPES.map((t) => {
              const c = pick(fill.by_type, "type", t.value) || {};
              const cell = fillCell(c.rate, c.booked, c.capacity);
              return {
                key: t.value,
                main: cell.main,
                sub: cell.sub,
                title: isNum(c.classes)
                  ? `${t.label} 수업 ${count(c.classes)}개 · 출석 기준 ${pct(
                      c.attended_rate
                    )}`
                  : null,
              };
            }),
          };
        })
        .reverse();
    },
    // 수업 종류 세 계열(Hybrid·Core·Cardio, 색 1·2·3번)
    fillSeries() {
      return CLASS_TYPES.map((t) => ({
        key: t.value,
        label: t.label,
        values: this.rows.map((r) => {
          const c = pick(r.fill && r.fill.by_type, "type", t.value);
          return c && isNum(c.rate) && c.capacity > 0 ? c.rate : null;
        }),
      }));
    },
    // ---------------- 요일 × 시작 시각 ----------------
    slots() {
      const list =
        this.period === "all"
          ? this.data && this.data.slots
          : this.data && this.data.recent_slots;
      return Array.isArray(list) ? list : [];
    },
    slotMap() {
      const map = {};
      for (const s of this.slots) {
        if (s) map[`${s.weekday}|${s.start_time}`] = s;
      }
      return map;
    },
    slotTimes() {
      const set = new Set(this.slots.map((s) => s && s.start_time));
      set.delete(undefined);
      return [...set].sort((a, b) => String(a).localeCompare(String(b)));
    },
    // 수업이 있는 요일만 칸으로(주말에 수업이 없으면 빈 칸을 늘어놓지 않는다)
    slotWeekdays() {
      const set = new Set(this.slots.map((s) => Number(s && s.weekday)));
      return [1, 2, 3, 4, 5, 6, 7].filter((d) => set.has(d));
    },
    heatRows() {
      return this.slotTimes.map((time) => ({
        time,
        cells: this.slotWeekdays.map((d) => ({
          key: d,
          ...this.heatCell(d, time),
        })),
      }));
    },
    periodText() {
      if (this.period === "all")
        return `고른 기간 전체(최근 ${this.months}개월)`;
      const from = this.data && this.data.recent_from;
      return from
        ? `최근 3개월(${monthLabelShort(from)}~이번 달)`
        : "최근 3개월";
    },
    measureLabel() {
      const found = SLOT_MEASURES.find((m) => m.value === this.measure);
      return found ? found.label : "";
    },
    heatLegend() {
      return HEAT_STEPS.map((s, i) => {
        const next = HEAT_STEPS[i + 1];
        const lo = Math.round(s.min * 100);
        const label = next
          ? i === 0
            ? `${Math.round(next.min * 100)}% 미만`
            : `${lo}~${Math.round(next.min * 100)}%`
          : `${lo}% 이상`;
        return { label, color: s.color };
      });
    },
    emptiest() {
      return emptiestSlots(this.slots, { measure: this.measure });
    },
    emptiestText() {
      return this.emptiest
        .map(
          (s) =>
            `${weekdayLabel(s.weekday)} ${s.start_time} ${heatPercent(
              slotRate(s, this.measure)
            )}`
        )
        .join(" · ");
    },
  },
  methods: {
    count,
    pct,
    num1,
    monthLabel,
    weekdayLabel,
    isNum,
    withUnit(text) {
      return text === "—" ? text : `${text}회`;
    },
    memberValue(obj, key) {
      if (!obj || this.prevNoData) return null;
      return obj[key];
    },
    // 칸 글자("74%")는 늘 보이고, 바탕은 파랑 순서 램프 5단. 지금 시간표에 없는 칸은 회색
    heatCell(weekday, time) {
      const s = this.slotMap[`${weekday}|${time}`];
      if (!s) return { text: "—", cls: "heat-none", style: null, title: null };
      const rate = slotRate(s, this.measure);
      const detail = `수업 ${count(s.classes)}개 · 예약 ${count(
        s.booked
      )} · 출석 ${count(s.attended)} / 정원 ${count(s.capacity)}`;
      const head = `${weekdayLabel(weekday)} ${time}`;
      if (rate === null) {
        return { text: "—", cls: "heat-none", style: null, title: detail };
      }
      if (s.in_schedule === false) {
        return {
          text: heatPercent(rate),
          cls: "heat-off",
          style: null,
          title: `${head} · ${NOT_IN_SCHEDULE_LABEL} · ${detail}`,
        };
      }
      const step = heatStep(rate);
      return {
        text: heatPercent(rate),
        cls: "",
        style: step ? { background: step.color, color: step.text } : null,
        title: `${head} · ${detail}`,
      };
    },
  },
};
</script>

<style scoped>
.operations {
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
}

/* ---------------- 타일 ---------------- */
.tiles {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.tile {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  padding: 12px 14px;
  border-radius: 10px;
  background: #0a0a0a;
  border: 1px solid #1f1f1f;
}

.tile-k {
  font-size: 13px;
  color: #8e8e8e;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

/* "(9월)"·"(9월 확정)": 카드 #151718 위 5.6:1(리뷰 3차: #6b7280은 3.7:1) */
.tile-k small {
  font-size: 12px;
  color: #8e8e8e;
}

.tile-v {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 2px 8px;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
  color: #fff;
}

.tile-v small {
  margin-left: 2px;
  font-size: 14px;
  font-weight: 400;
  color: #8e8e8e;
}

.tile-v.muted {
  color: #9ca3af;
}

.tile-extra {
  font-size: 13px;
  font-weight: 400;
  color: #c8c8c8;
}

/* 소진율이 표본 적음(회색)이면 같은 횟수권으로 낸 "평균 n회 남김"도 회색(리뷰 5차) */
.tile-v.muted .tile-extra {
  color: #9ca3af;
}

.tile-sub {
  font-size: 12px;
  line-height: 1.45;
  color: #c8c8c8;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

/* 종류별 소진율 중 분모 5 미만(표본 적음)은 회색 */
.tile-sub .small-part {
  color: #9ca3af;
}

/* 종류 하나("20회권 33.3% (2/6)")는 한 덩어리: 줄은 " · "에서만 바꾼다(리뷰 6차: 1024~1440px에서 "(2/6)"만 다음 줄로 떨어져
   바로 위 "(6/14)"와 어느 비율의 것인지 헷갈렸다) */
.tile-sub .type-part {
  white-space: nowrap;
}

.tile-def {
  margin-top: auto;
  padding-top: 4px;
  font-size: 12px;
  line-height: 1.4;
  color: #8e8e8e;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

/* ---------------- 묶음 ---------------- */
.block {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.block-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 12px;
}

.block h3,
.chart-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
}

.block-def {
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: #9d9d9d;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

.slot-controls {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 14px;
}

.seg {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.seg-k {
  font-size: 12px;
  color: #8e8e8e;
}

.seg-btn {
  height: 30px;
  padding: 0 10px;
  border-radius: 6px;
  border: 1px solid #333;
  background: #1b1d1e;
  color: #9d9d9d;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}

.seg-btn:hover:not(.active) {
  background: #252728;
  color: #fff;
}

.seg-btn.active {
  background: #78ff47;
  border-color: #78ff47;
  color: #000;
  font-weight: 600;
}

.seg-btn:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 1px;
}

/* ---------------- 월별 표 ---------------- */
.table-wrap {
  width: 100%;
  min-width: 0;
  overflow-x: auto;
  border: 1px solid #1f1f1f;
  border-radius: 8px;
  background: #0a0a0a;
}

.table-wrap:focus-visible {
  outline: 2px solid #78ff47;
  outline-offset: 2px;
}

.t-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}

.t-table th,
.t-table td {
  padding: 8px 10px;
  border-bottom: 1px solid #1f1f1f;
  text-align: right;
  white-space: nowrap;
  vertical-align: top;
}

.t-table thead th {
  font-size: 12px;
  font-weight: 400;
  color: #8e8e8e;
  background: #0a0a0a;
  vertical-align: bottom;
}

.t-table tbody tr:last-child th,
.t-table tbody tr:last-child td {
  border-bottom: 0;
}

.t-table tbody tr:hover td,
.t-table tbody tr:hover th {
  background: #121314;
}

.t-table .col-month {
  position: sticky;
  left: 0;
  z-index: 1;
  text-align: left;
  background: #0a0a0a;
  color: #fff;
  font-weight: 600;
  box-shadow: 1px 0 0 #1f1f1f;
}

.t-table thead .col-month {
  color: #8e8e8e;
  font-weight: 400;
}

.month {
  margin-right: 6px;
}

.t-table .rate {
  display: block;
}

.t-table .sub {
  display: block;
  margin-top: 2px;
  font-size: 11px;
  color: #8e8e8e;
}

.t-table td.muted .rate {
  color: #9ca3af;
}

.badge {
  display: inline-flex;
  align-items: center;
  height: 18px;
  padding: 0 6px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  vertical-align: middle;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.06);
  color: #c8c8c8;
}

/* 이번 달이 기록 전이면(수업만 있는 DB) "진행 중"·"기록 전" 두 배지가 붙지 않게(빌드가 둘 사이 빈칸을 지운다, 리뷰 6차) */
.badge + .badge {
  margin-left: 4px;
}

.badge.tone-muted {
  color: #9ca3af;
  background: rgba(156, 163, 175, 0.1);
  border-color: rgba(156, 163, 175, 0.3);
}

/* ---------------- 요일 × 시각 색 표 ---------------- */
.heat-wrap {
  width: auto;
  max-width: 100%;
  align-self: flex-start;
}

.heat {
  border-collapse: separate;
  border-spacing: 2px;
  padding: 4px;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}

.heat th {
  padding: 4px 6px;
  font-weight: 400;
  color: #8e8e8e;
  text-align: center;
}

.heat .heat-time {
  position: sticky;
  left: 0;
  background: #0a0a0a;
  text-align: right;
  color: #c8c8c8;
}

.heat-cell {
  min-width: 46px;
  height: 30px;
  padding: 0 6px;
  border-radius: 4px;
  text-align: center;
  font-weight: 600;
  white-space: nowrap;
}

/* "—"(수업 없음)도 읽는 글자라 #0a0a0a 위 6:1(리뷰 5차: #4b5563은 2.6:1) */
.heat-none {
  color: #8e8e8e;
  background: transparent;
  font-weight: 400;
}

.heat-off {
  background: #26292a;
  color: #9ca3af;
  font-weight: 400;
}

.heat-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: 12px;
  color: #c8c8c8;
}

.heat-legend li {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.swatch {
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 3px;
}

.swatch--off {
  background: #26292a;
  border: 1px solid #3a3d3e;
}

.dash {
  color: #8e8e8e;
}

.emptiest {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #e5e5e5;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

.emptiest-k {
  margin-right: 8px;
  color: #8e8e8e;
}

.slot-empty {
  margin: 0;
  padding: 20px 12px;
  border-radius: 8px;
  border: 1px dashed #2a2a2a;
  text-align: center;
  font-size: 13px;
  color: #c8c8c8;
}

/* ---------------- 차트 ---------------- */
.chart-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  padding: 12px 14px;
  border-radius: 10px;
  background: #151718;
  border: 1px solid #1f1f1f;
}

.chart-title {
  font-size: 14px;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* ---------------- 반응형 ---------------- */
@media (max-width: 1280px) {
  .tile-v {
    font-size: 20px;
  }
}

@media (max-width: 960px) {
  .tiles {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 720px) {
  .tiles {
    gap: 8px;
  }

  .tile {
    padding: 10px 12px;
  }

  .t-table th,
  .t-table td {
    padding: 7px 8px;
  }

  .heat-cell {
    min-width: 38px;
    padding: 0 3px;
  }

  .heat th {
    padding: 4px 3px;
  }
}
</style>
